import type { Sessao } from '../types/gmud'

const CHAVE = 'gestao2.sessao'

/** Disparado quando a sessão cai (renovação recusada, logout). A aplicação reage voltando ao login. */
export const EVENTO_SESSAO_ENCERRADA = 'gestao2:sessao-encerrada'

/**
 * Disparado quando uma sessão nova é gravada: login por senha, retorno do Entra ID, renovação.
 *
 * É o par que faltava do evento acima, e a falta se via na tela: quem entrava por usuário e senha
 * gravava o token e continuava olhando o formulário de login, porque o `ProvedorDeSessao` só
 * descobria token na carga do módulo. Não dava para resolver com o evento `storage` do navegador —
 * ele avisa as OUTRAS abas, nunca a aba que escreveu.
 */
export const EVENTO_SESSAO_INICIADA = 'gestao2:sessao-iniciada'

/**
 * Onde a sessão mora.
 *
 * `localStorage` e não cookie porque o backend é sem estado e o token é lido pelo
 * JavaScript para montar o cabeçalho `Authorization`. A troca dessa escolha por cookie
 * `HttpOnly` é possível e desejável, mas exige CSRF do lado do servidor — decisão de
 * backend, não de tela. O que dá para fazer aqui já está feito: nada de
 * `dangerouslySetInnerHTML` (ESLint barra) e nada de token em query string.
 */
export function lerSessao(): Sessao | null {
  try {
    const bruto = window.localStorage.getItem(CHAVE)
    if (!bruto) {
      return null
    }
    const sessao = JSON.parse(bruto) as Sessao
    return sessao.accessToken ? sessao : null
  } catch {
    // Conteúdo corrompido não pode travar a aplicação: trata-se como deslogado.
    return null
  }
}

/** Grava a sessão recém-emitida e avisa a aplicação. */
export function gravarSessao(sessao: Sessao): void {
  window.localStorage.setItem(CHAVE, JSON.stringify(sessao))
  window.dispatchEvent(new Event(EVENTO_SESSAO_INICIADA))
}

/** Apaga a sessão e avisa a aplicação. */
export function encerrarSessao(): void {
  window.localStorage.removeItem(CHAVE)
  window.dispatchEvent(new Event(EVENTO_SESSAO_ENCERRADA))
}

/** @returns o token de acesso corrente, ou nulo quando não há sessão. */
export function tokenDeAcesso(): string | null {
  return lerSessao()?.accessToken ?? null
}

/**
 * Traduz o que acontece com a sessão NAS OUTRAS ABAS para os mesmos eventos desta.
 *
 * O evento `storage` do navegador só dispara nas abas que não escreveram — é o complemento
 * exato dos `dispatchEvent` acima, que só a aba que escreveu ouve. Sem esta ponte, sair numa
 * aba deixava a outra "logada" com o cache de quem saiu, sem caminho de volta ao login; e
 * entrar numa aba deixava a outra parada na tela de login com a sessão já válida.
 *
 * @returns função que desliga a escuta (para o `useEffect`)
 */
export function espelharSessaoDeOutrasAbas(): () => void {
  const aoMudar = (evento: StorageEvent) => {
    // `key === null` é um `clear()` do storage inteiro — a sessão foi junto.
    if (evento.key !== null && evento.key !== CHAVE) {
      return
    }
    const entrou = evento.key !== null && evento.newValue !== null
    window.dispatchEvent(new Event(entrou ? EVENTO_SESSAO_INICIADA : EVENTO_SESSAO_ENCERRADA))
  }
  window.addEventListener('storage', aoMudar)
  return () => window.removeEventListener('storage', aoMudar)
}

/** O que veio no fragmento da URL depois do login. */
export interface RetornoDoLogin {
  sessao?: Sessao
  erro?: string
}

/**
 * Lê o retorno do login e limpa a URL.
 *
 * O backend devolve os tokens no FRAGMENTO (`#accessToken=…`), que não é enviado ao
 * servidor nem entra em log de proxy, `Referer` ou histórico de acesso. Aqui a
 * contrapartida: o fragmento tem de sair da barra de endereços assim que for lido, ou
 * o token acaba num print de tela ou num favorito.
 *
 * @returns o que foi lido, ou nulo quando não havia retorno de login na URL
 */
export function capturarRetornoDoLogin(): RetornoDoLogin | null {
  const fragmento = window.location.hash.replace(/^#/, '')
  if (!fragmento) {
    return null
  }

  const parametros = new URLSearchParams(fragmento)
  const accessToken = parametros.get('accessToken')
  const refreshToken = parametros.get('refreshToken')
  const erro = parametros.get('erro')
  if (!accessToken && !erro) {
    return null
  }

  limparFragmento()

  if (accessToken && refreshToken) {
    return {
      sessao: {
        accessToken,
        refreshToken,
        // Mesmo valor que `TokenDeSessaoService.Sessao.tipo` traz na renovação: é o
        // esquema de autenticação, não o tipo do token. Divergir aqui faria a sessão
        // vinda do login e a vinda do refresh terem formas diferentes em memória.
        tipo: 'Bearer',
        expiraEmSegundos: Number(parametros.get('expiraEm') ?? 0),
      },
    }
  }
  if (accessToken) {
    // Metade da sessão não é sessão: sem o token de renovação, o acesso morre em 15
    // minutos sem chance de renovar. Como o fragmento já foi apagado, engolir isto
    // devolveria o usuário à tela de login sem uma linha explicando o que houve.
    return { erro: 'O login voltou incompleto. Clique em entrar novamente.' }
  }
  return { erro: erro ?? undefined }
}

function limparFragmento(): void {
  const limpa = window.location.pathname + window.location.search
  window.history.replaceState(null, '', limpa)
}
